/**
 * Persona Card renderer (artifact_name: `persona_card`).
 * JSON: { main_information: Field[4], behavior_information: Field[4] }.
 */

import React from 'react';
import {
  CardShell,
  LabelValueRow,
  ScoreHeader,
  SectionCard,
  type ValueVariant,
} from './shared/components';
import { gapsCount, weightedScore } from './shared/evidence';
import {
  fieldToJson,
  fieldToMarkdown,
  parseFields,
  splitValue,
  type StructuredField,
} from './shared/field';
import { esc, htmlDoc, scoreHtml, tagSpanHtml } from './shared/html';
import { TagToggle } from './shared/TagToggle';
import type { ArtifactRenderer } from './types';

export interface PersonaModel {
  mainInformation: StructuredField[];
  behaviorInformation: StructuredField[];
}

/**
 * Decide how a field's value renders from the value itself (not the label),
 * so it works regardless of language. A bulleted value (`"- a\n- b"` / `"* a"`)
 * renders as compact chips when every item is short, a bullet list when items
 * are longer, and plain prose otherwise. Field labels are localized (Arabic or
 * English), so we must never key the layout on the label text.
 */
const variantForValue = (value: string): ValueVariant => {
  const { items, bulleted } = splitValue(value);
  if (!bulleted || items.length < 2) return 'text';
  return items.every((it) => it.length <= 40) ? 'chips' : 'list';
};

// -- parse / serialize -------------------------------------------------------

export function parsePersona(json: Record<string, unknown>): PersonaModel | null {
  // Persona is identified structurally by its two field arrays — `main_information`
  // and `behavior_information`. These keys are unique to the persona schema, so the
  // check is language-neutral: field *labels* may be Arabic (الدور، السياق) or
  // English (Role, Context) and still parse. Never key on a specific label.
  if (!Array.isArray(json.main_information) && !Array.isArray(json.behavior_information)) {
    return null;
  }
  const mainInformation = parseFields(json.main_information);
  const behaviorInformation = parseFields(json.behavior_information);
  if (mainInformation.length === 0 && behaviorInformation.length === 0) return null;
  return { mainInformation, behaviorInformation };
}

export function personaToJson(model: PersonaModel): Record<string, unknown> {
  return {
    main_information: model.mainInformation.map(fieldToJson),
    behavior_information: model.behaviorInformation.map(fieldToJson),
  };
}

const allFields = (m: PersonaModel) => [...m.mainInformation, ...m.behaviorInformation];

const depth = (m: PersonaModel) => {
  const fields = allFields(m);
  const score = weightedScore(fields);
  const gaps = gapsCount(fields);
  const summary =
    gaps > 0
      ? `${gaps} of ${fields.length} fields need stronger evidence — validate before designing against them.`
      : 'Fully evidenced.';
  return { score, summary };
};

export function personaToMarkdown(model: PersonaModel): string {
  const blocks = allFields(model).map(fieldToMarkdown);
  return blocks.join('\n\n');
}

// -- card --------------------------------------------------------------------

const valuesFor = (f: StructuredField): string[] => {
  const { items } = splitValue(f.value);
  return items.length ? items : [f.value].filter(Boolean);
};

const PersonaCard: React.FC<{ model: PersonaModel }> = ({ model }) => {
  const { score, summary } = depth(model);
  return (
    <CardShell testId="persona-artifact-card">
      <ScoreHeader score={score} label="Depth score" tone="amber" summary={summary} />
      <SectionCard title="Main Information">
        {model.mainInformation.map((f) => (
          <LabelValueRow
            key={f.field}
            label={f.field}
            values={valuesFor(f)}
            variant={variantForValue(f.value)}
            tag={f.tag}
          />
        ))}
      </SectionCard>
      <SectionCard title="Behavior information">
        {model.behaviorInformation.map((f) => (
          <LabelValueRow
            key={f.field}
            label={f.field}
            values={valuesFor(f)}
            variant={variantForValue(f.value)}
            tag={f.tag}
          />
        ))}
      </SectionCard>
    </CardShell>
  );
};

// -- editor ------------------------------------------------------------------

const inputCls =
  'w-full resize-y rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100';

const PersonaEditor: React.FC<{
  model: PersonaModel;
  onReady: (getJson: () => Record<string, unknown>) => void;
}> = ({ model, onReady }) => {
  const [state, setState] = React.useState<PersonaModel>(model);
  const stateRef = React.useRef(state);
  stateRef.current = state;

  React.useEffect(() => {
    onReady(() => personaToJson(stateRef.current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patch = (
    section: 'mainInformation' | 'behaviorInformation',
    i: number,
    p: Partial<StructuredField>
  ) =>
    setState((prev) => ({
      ...prev,
      [section]: prev[section].map((f, j) => (j === i ? { ...f, ...p } : f)),
    }));

  const renderSection = (title: string, section: 'mainInformation' | 'behaviorInformation') => (
    <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700 sm:p-5 bg-surface">
      <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {title}
      </h3>
      <div className="divide-y divide-gray-100 dark:divide-gray-800">
        {state[section].map((f, i) => {
          const isList = splitValue(f.value).bulleted;
          return (
            <div key={f.field} className="py-2.5">
              <div className="mb-1.5 flex items-center gap-2">
                <label className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {f.field}
                </label>
                <TagToggle value={f.tag} onChange={(tag) => patch(section, i, { tag })} />
                {isList && <span className="text-[11px] text-gray-400">one item per line</span>}
              </div>
              <textarea
                value={f.value}
                rows={isList ? 3 : 2}
                onChange={(e) => patch(section, i, { value: e.target.value })}
                className={inputCls}
              />
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <CardShell>
      {renderSection('Main Information', 'mainInformation')}
      {renderSection('Behavior information', 'behaviorInformation')}
    </CardShell>
  );
};

// -- html --------------------------------------------------------------------

const rowHtml = (f: StructuredField): string => {
  const values = valuesFor(f);
  const variant = variantForValue(f.value);
  let valueHtml: string;
  if (variant === 'chips')
    valueHtml = `<div class="chips">${values.map((v) => `<span class="chip">${esc(v)}</span>`).join('')}</div>`;
  else if (variant === 'list' && values.length > 1)
    valueHtml = `<ul>${values.map((v) => `<li>${esc(v)}</li>`).join('')}</ul>`;
  else valueHtml = `<p>${esc(values.join(' ') || '—')}</p>`;
  return `<div class="row"><div class="label">${esc(f.field)} ${tagSpanHtml(f.tag)}</div><div class="value">${valueHtml}</div></div>`;
};

export function personaToHtml(model: PersonaModel, title = 'Persona'): string {
  const { score, summary } = depth(model);
  const body = `${scoreHtml(score, 'Depth score', 'amber', summary)}
    <section class="card"><h3>Main Information</h3>${model.mainInformation.map(rowHtml).join('')}</section>
    <section class="card"><h3>Behavior information</h3>${model.behaviorInformation.map(rowHtml).join('')}</section>`;
  return htmlDoc(title, body);
}

export const personaRenderer: ArtifactRenderer<PersonaModel> = {
  type: 'persona_card',
  matchNames: ['persona_card', 'persona'],
  parse: parsePersona,
  toJson: personaToJson,
  toMarkdown: personaToMarkdown,
  toHtml: personaToHtml,
  Card: PersonaCard,
  Editor: PersonaEditor,
};
